import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useUIStore } from './ui'
import apiService from '../services/apiService'
import logService from '../services/logService'
import {
  ORDER_STATUS_LIST,
  PAYMENT_STATUS_LIST,
  getOrderStatusByValue,
  getPaymentStatusByValue,
  formatCurrency,
  CURRENCY
} from '../constants/orderConstants'

export const useOrderStore = defineStore('orders', () => {
  const uiStore = useUIStore()

  // State
  const orders = ref([])
  const currentOrder = ref(null)
  const loading = ref(false)
  const pagination = ref({
    total: 0,
    current_page: 1,
    last_page: 1,
    per_page: 15
  })

  // Use centralized order statuses
  const orderStatuses = ORDER_STATUS_LIST
  const paymentStatuses = PAYMENT_STATUS_LIST

  // API Functions
  const fetchOrders = async (filters = {}) => {
    try {
      loading.value = true
      logService.info('Fetching orders...', filters)

      const response = await apiService.get('/orders', filters)

      if (response.success) {
        orders.value = response.data
        if (response.meta) {
          pagination.value = {
            total: response.meta.total || 0,
            current_page: response.meta.current_page || 1,
            last_page: response.meta.last_page || 1,
            per_page: response.meta.per_page || 15
          }
        }
        return response.data
      } else {
        throw new Error(response.message || 'Failed to fetch orders')
      }
    } catch (error) {
      logService.error('Fetch Orders Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Failed to Load Orders',
        message: error.message
      })
      throw error
    } finally {
      loading.value = false
    }
  }

  const fetchOrder = async (orderId) => {
    try {
      loading.value = true
      logService.info(`Fetching order: ${orderId}`)

      const response = await apiService.get(`/orders/${orderId}`)

      if (response.success) {
        currentOrder.value = response.data

        // Update the order in the orders array if it exists
        const index = orders.value.findIndex(order => order.id === parseInt(orderId))
        if (index !== -1) {
          orders.value[index] = response.data
        }

        return response.data
      } else {
        throw new Error(response.message || 'Order not found')
      }
    } catch (error) {
      logService.error(`Fetch Order Error [${orderId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Failed to Load Order',
        message: error.message
      })
      throw error
    } finally {
      loading.value = false
    }
  }

  const createOrder = async (orderData) => {
    try {
      loading.value = true
      logService.info('Creating order...', orderData)

      const response = await apiService.post('/orders', orderData)

      if (response.success) {
        orders.value.unshift(response.data)
        currentOrder.value = response.data

        uiStore.showNotification({
          type: 'success',
          title: 'Order Created',
          message: `Your order ${response.data.order_number} has been created successfully.`
        })

        return response.data
      } else {
        throw new Error(response.message || 'Failed to create order')
      }
    } catch (error) {
      logService.error('Create Order Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Order Creation Failed',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  const updateOrder = async (id, orderData) => {
    try {
      loading.value = true
      logService.info(`Updating order: ${id}`, orderData)

      const response = await apiService.put(`/orders/${id}`, orderData)

      if (response.success) {
        const index = orders.value.findIndex(order => order.id === id)
        if (index !== -1) {
          orders.value[index] = response.data
        }

        if (currentOrder.value?.id === id) {
          currentOrder.value = response.data
        }

        uiStore.showNotification({
          type: 'success',
          title: 'Order Updated',
          message: 'Order has been updated successfully.'
        })

        return response.data
      } else {
        throw new Error(response.message || 'Failed to update order')
      }
    } catch (error) {
      logService.error(`Update Order Error [${id}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Update Failed',
        message: error.message
      })
      return null
    } finally {
      loading.value = false
    }
  }

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      loading.value = true
      logService.info(`Updating order status: ${orderId} -> ${newStatus}`)

      const response = await apiService.put(`/orders/${orderId}/status`, { status: newStatus })

      if (response.success) {
        // Update the order in the local state
        const orderIndex = orders.value.findIndex(order => order.id === parseInt(orderId))
        if (orderIndex !== -1) {
          orders.value[orderIndex].status = newStatus
        }

        // Update current order if it's the same
        if (currentOrder.value && currentOrder.value.id === parseInt(orderId)) {
          currentOrder.value.status = newStatus
        }

        return response.data
      } else {
        throw new Error(response.message || 'Failed to update order status')
      }
    } catch (error) {
      logService.error(`Update Order Status Error [${orderId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Status Update Failed',
        message: error.message
      })
      throw error
    } finally {
      loading.value = false
    }
  }

  // Update Payment Status
  const updatePaymentStatus = async (orderId, paymentStatus, paidAmount = null) => {
    try {
      loading.value = true
      logService.info(`Updating payment status: ${orderId} -> ${paymentStatus}`)

      const payload = { payment_status: paymentStatus }
      if (paidAmount !== null) {
        payload.paid_amount = paidAmount
      }

      const response = await apiService.put(`/orders/${orderId}/payment-status`, payload)

      if (response.success) {
        // Update the order in the local state
        const orderIndex = orders.value.findIndex(order => order.id === parseInt(orderId))
        if (orderIndex !== -1) {
          orders.value[orderIndex].payment_status = paymentStatus
          if (paidAmount !== null) {
            orders.value[orderIndex].paid_amount = paidAmount
          }
        }

        // Update current order if it's the same
        if (currentOrder.value && currentOrder.value.id === parseInt(orderId)) {
          currentOrder.value.payment_status = paymentStatus
          if (paidAmount !== null) {
            currentOrder.value.paid_amount = paidAmount
          }
        }

        uiStore.showNotification({
          type: 'success',
          title: 'Payment Status Updated',
          message: `Payment status changed to ${getPaymentStatusByValue(paymentStatus).label}`
        })

        return response.data
      } else {
        throw new Error(response.message || 'Failed to update payment status')
      }
    } catch (error) {
      logService.error(`Update Payment Status Error [${orderId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Payment Status Update Failed',
        message: error.message
      })
      throw error
    } finally {
      loading.value = false
    }
  }

  // Developer Assignment
  const assignDeveloper = async (orderId, developerId) => {
    try {
      loading.value = true
      logService.info(`Assigning developer ${developerId} to order ${orderId}`)

      const response = await apiService.post(`/orders/${orderId}/assign-developer`, { developer_id: developerId })

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Developer Assigned',
          message: 'Developer has been assigned successfully.'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to assign developer')
      }
    } catch (error) {
      logService.error(`Assign Developer Error:`, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const removeDeveloper = async (orderId, developerId) => {
    try {
      loading.value = true
      logService.info(`Removing developer ${developerId} from order ${orderId}`)

      const response = await apiService.post(`/orders/${orderId}/remove-developer`, { developer_id: developerId })

      if (response.success) {
        uiStore.showNotification({
          type: 'success',
          title: 'Developer Removed',
          message: 'Developer has been removed successfully.'
        })
        return response.data
      } else {
        throw new Error(response.message || 'Failed to remove developer')
      }
    } catch (error) {
      logService.error(`Remove Developer Error:`, error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Getters
  const getOrderById = (id) => {
    return orders.value.find(order => order.id === id)
  }

  const getOrdersByStatus = (status) => {
    return orders.value.filter(order => order.status === status)
  }

  const getPendingOrders = () => {
    return orders.value.filter(order => order.status === 'pending')
  }

  const getActiveOrders = () => {
    return orders.value.filter(order => ['pending', 'processing'].includes(order.status))
  }

  const getCompletedOrders = () => {
    return orders.value.filter(order => order.status === 'completed')
  }

  const getTotalRevenue = () => {
    return orders.value
      .filter(order => order.status === 'completed')
      .reduce((total, order) => total + parseFloat(order.total_amount || 0), 0)
  }

  // Invoice functions
  const downloadInvoice = async (orderId) => {
    try {
      logService.info(`Downloading invoice for order: ${orderId}`)
      const response = await apiService.get(`/orders/${orderId}/invoice/download`, {}, { responseType: 'blob' })

      if (response) {
        const url = window.URL.createObjectURL(new Blob([response]))
        const a = document.createElement('a')
        a.href = url
        a.download = `invoice-${orderId}.pdf`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)

        uiStore.showNotification({
          type: 'success',
          title: 'Invoice Downloaded',
          message: 'Invoice has been downloaded successfully'
        })
      } else {
        throw new Error('Failed to download invoice')
      }
    } catch (error) {
      logService.error(`Download Invoice Error [${orderId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Download Failed',
        message: error.message || 'Failed to download invoice'
      })
      throw error
    }
  }

  const getInvoiceData = async (orderId) => {
    try {
      logService.info(`Fetching invoice data for order: ${orderId}`)
      const response = await apiService.get(`/orders/${orderId}/invoice`)

      if (response.success) {
        return response.data
      } else {
        throw new Error(response.message || 'Failed to fetch invoice data')
      }
    } catch (error) {
      logService.error(`Fetch Invoice Data Error [${orderId}]:`, error)
      uiStore.showNotification({
        type: 'error',
        title: 'Failed to Load Invoice',
        message: error.message
      })
      throw error
    }
  }

  const openWhatsApp = (message = '') => {
    // We can't use useSettingsStore directly here to avoid circular dependencies if any, 
    // but we can assume settings are available in the Window or provided
    const whatsappNumber = localStorage.getItem('whatsapp_number') || '+8801974611004'
    const cleanNumber = whatsappNumber.replace(/[^0-9]/g, '')
    const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`
    window.open(whatsappUrl, '_blank')
  }

  const exportOrdersCsv = async (filters = {}) => {
    try {
      loading.value = true
      logService.info('Exporting orders to CSV...', filters)

      // Use apiService.get with responseType: 'blob'
      const response = await apiService.get('/orders/export', filters, { responseType: 'blob' })

      if (response) {
        const url = window.URL.createObjectURL(new Blob([response]))
        const a = document.createElement('a')
        a.href = url
        a.download = `orders-${new Date().getTime()}.csv`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)

        uiStore.showNotification({
          type: 'success',
          title: 'Export Successful',
          message: 'Your CSV file has been downloaded.'
        })
      } else {
        throw new Error('Failed to export orders')
      }
    } catch (error) {
      logService.error('Export Orders Error:', error)
      uiStore.showNotification({
        type: 'error',
        title: 'Export Failed',
        message: error.message || 'Failed to export orders'
      })
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    orders,
    currentOrder,
    loading,
    orderStatuses,
    paymentStatuses,

    // Actions
    fetchOrders,
    fetchOrder,
    createOrder,
    updateOrder,
    updateOrderStatus,
    updatePaymentStatus,
    assignDeveloper,
    removeDeveloper,
    downloadInvoice,
    getInvoiceData,
    openWhatsApp,
    exportOrdersCsv,

    // Getters
    getOrderById,
    getOrdersByStatus,
    getPendingOrders,
    getActiveOrders,
    getTotalRevenue,
    pagination,

    // Helper functions from constants
    getOrderStatusByValue,
    getPaymentStatusByValue,
    formatCurrency,
    CURRENCY
  }
})

